import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Tooltip, CircleMarker, useMap, useMapEvents, ScaleControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import mapStartIcon from '../assets/map-start.svg';
import mapPlaneIcon from '../assets/map-plane.svg';
import mapBallonIcon from '../assets/map-ballon.svg';
import {
  GPS_ALTITUDE_KEY,
  getChannelConfig,
  transformChannelValue,
} from '../utils/sensorUtils';

interface Coordinate {
  latitude: number;
  longitude: number;
  altitude: number;
  timestamp: string;
}

interface MeasurementResult {
  timestamp: string;
  channel: string;
  value: number;
  aircraftId: string;
  flightTrackId: string;
}

interface RouteMapProps {
  coordinates: Coordinate[];
  aircraftType?: string;
  height?: string;
  measurements?: MeasurementResult[];
  showSensorOverlay?: boolean;
  hoverTimestamp?: number | null;
}

const findNearestValue = (
  timestamp: string,
  channelMeasurements: { timeMs: number; value: number }[]
): number | undefined => {
  if (channelMeasurements.length === 0) return undefined;
  const t = new Date(timestamp).getTime();
  return channelMeasurements.reduce((nearest, m) =>
    Math.abs(m.timeMs - t) < Math.abs(nearest.timeMs - t) ? m : nearest
  ).value;
};

const valueToColor = (value: number, min: number, max: number): string => {
  const t = max === min ? 0.5 : (value - min) / (max - min);
  const hue = Math.round(240 - t * 240);
  return `hsl(${hue}, 80%, 45%)`;
};

// Dismiss callout when clicking the map background
const DismissCallout: React.FC<{ onDismiss: () => void }> = ({ onDismiss }) => {
  useMapEvents({ click: onDismiss });
  return null;
};

// Component to fit map bounds to the flight path
const FitBounds: React.FC<{ coordinates: Coordinate[] }> = ({ coordinates }) => {
  const map = useMap();

  useEffect(() => {
    if (coordinates.length > 0) {
      const bounds = coordinates.map(c => [c.latitude, c.longitude] as [number, number]);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [coordinates, map]);

  return null;
};

interface FlightPathLayerProps {
  coloredSegments: Array<{ positions: [number, number][]; color: string }> | null;
  pathCoordinates: [number, number][];
  onLineClick: (e: L.LeafletMouseEvent) => void;
  selectedChannel: string;
}

const FlightPathLayer: React.FC<FlightPathLayerProps> = React.memo(({
  coloredSegments,
  pathCoordinates,
  onLineClick,
  selectedChannel,
}) => {
  if (coloredSegments) {
    return (
      <>
        {coloredSegments.map((seg, i) => (
          <Polyline
            key={`${selectedChannel}-${i}`}
            positions={seg.positions}
            color={seg.color}
            weight={5}
            opacity={0.9}
            bubblingMouseEvents={false}
            eventHandlers={{ click: onLineClick }}
          />
        ))}
      </>
    );
  }

  return (
    <Polyline
      positions={pathCoordinates}
      color="#0d6efd"
      weight={3}
      opacity={0.8}
      dashArray="5, 5"
      bubblingMouseEvents={false}
      eventHandlers={{ click: onLineClick }}
    />
  );
});

const RouteMap: React.FC<RouteMapProps> = ({
  coordinates,
  aircraftType = 'flugzeug',
  height = '300px',
  measurements,
  showSensorOverlay = false,
  hoverTimestamp,
}) => {
  const [sliderValue, setSliderValue] = useState(0);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [calloutIndex, setCalloutIndex] = useState<number | null>(null);

  const sensorChannels = Array.from(new Set((measurements ?? []).map(m => m.channel)));
  const channels = [GPS_ALTITUDE_KEY, ...sensorChannels];

  // Auto-select GPS altitude by default
  useEffect(() => {
    if (!selectedChannel) {
      setSelectedChannel(GPS_ALTITUDE_KEY);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate center point
  const centerLat = coordinates.length > 0 ? coordinates[Math.floor(coordinates.length / 2)].latitude : 0;
  const centerLon = coordinates.length > 0 ? coordinates[Math.floor(coordinates.length / 2)].longitude : 0;

  // Convert coordinates to Leaflet format [lat, lng]
  const pathCoordinates = React.useMemo(() => coordinates.map(c => [c.latitude, c.longitude] as [number, number]), [coordinates]);

  // Get start and end times
  const startTime = coordinates.length > 0 ? new Date(coordinates[0].timestamp) : new Date();
  const endTime = coordinates.length > 0 ? new Date(coordinates[coordinates.length - 1].timestamp) : new Date();

  // Pre-parse coordinate timestamps for fast lookup
  const coordTimes = React.useMemo(() => {
    return coordinates.map(c => new Date(c.timestamp).getTime());
  }, [coordinates]);

  const getInterpolatedPositionByTime = React.useCallback((targetTimeMs: number) => {
    if (coordinates.length === 0) {
      const emptyCoord = { latitude: 0, longitude: 0, altitude: 0, timestamp: '' };
      return { coordinate: emptyCoord, lowerCoord: emptyCoord, sliderPercent: 0 };
    }
    if (coordinates.length === 1 || targetTimeMs <= coordTimes[0]) {
      return { coordinate: coordinates[0], lowerCoord: coordinates[0], sliderPercent: 0 };
    }
    if (targetTimeMs >= coordTimes[coordTimes.length - 1]) {
      const last = coordinates[coordinates.length - 1];
      return { coordinate: last, lowerCoord: last, sliderPercent: 100 };
    }

    let low = 0;
    let high = coordTimes.length - 1;
    while (low <= high) {
      const mid = (low + high) >> 1;
      if (coordTimes[mid] <= targetTimeMs) {
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }
    const i = Math.max(0, Math.min(coordTimes.length - 2, high));
    const t0 = coordTimes[i];
    const t1 = coordTimes[i + 1];
    const fraction = t1 > t0 ? (targetTimeMs - t0) / (t1 - t0) : 0;
    const clampedFraction = Math.max(0, Math.min(1, fraction));

    const c0 = coordinates[i];
    const c1 = coordinates[i + 1];

    const interpolatedCoord: Coordinate = {
      latitude: c0.latitude + (c1.latitude - c0.latitude) * clampedFraction,
      longitude: c0.longitude + (c1.longitude - c0.longitude) * clampedFraction,
      altitude: c0.altitude + (c1.altitude - c0.altitude) * clampedFraction,
      timestamp: new Date(targetTimeMs).toISOString(),
    };

    const sliderPercent = ((i + clampedFraction) / (coordinates.length - 1)) * 100;

    return {
      coordinate: interpolatedCoord,
      lowerCoord: c0,
      sliderPercent,
    };
  }, [coordinates, coordTimes]);

  // Calculate current position: from hoverTimestamp if hovering, or from sliderValue
  const activePosition = React.useMemo(() => {
    if (hoverTimestamp !== null && hoverTimestamp !== undefined && coordinates.length > 0) {
      return getInterpolatedPositionByTime(hoverTimestamp);
    }

    const sliderPosition = coordinates.length > 0 ? (sliderValue / 100) * (coordinates.length - 1) : 0;
    const lowerIndex = Math.floor(sliderPosition);
    const upperIndex = Math.min(lowerIndex + 1, Math.max(0, coordinates.length - 1));
    const fraction = sliderPosition - lowerIndex;

    const lower = coordinates.length > 0 ? coordinates[lowerIndex] : { latitude: 0, longitude: 0, altitude: 0, timestamp: '' };
    const upper = coordinates.length > 0 ? coordinates[upperIndex] : { latitude: 0, longitude: 0, altitude: 0, timestamp: '' };
    const current = coordinates.length > 0 ? {
      latitude: lower.latitude + (upper.latitude - lower.latitude) * fraction,
      longitude: lower.longitude + (upper.longitude - lower.longitude) * fraction,
      altitude: lower.altitude + (upper.altitude - lower.altitude) * fraction,
      timestamp: lower.timestamp
    } : { latitude: 0, longitude: 0, altitude: 0, timestamp: '' };

    return {
      coordinate: current,
      lowerCoord: lower,
      sliderPercent: sliderValue,
    };
  }, [hoverTimestamp, sliderValue, coordinates, getInterpolatedPositionByTime]);

  const currentCoordinate = activePosition.coordinate;
  const lowerCoord = activePosition.lowerCoord;
  const displaySliderValue = hoverTimestamp !== null && hoverTimestamp !== undefined
    ? activePosition.sliderPercent
    : sliderValue;

  const lastSliderPercentRef = React.useRef(sliderValue);
  if (hoverTimestamp !== null && hoverTimestamp !== undefined) {
    lastSliderPercentRef.current = activePosition.sliderPercent;
  }

  // When hover stops, commit the last hovered position to sliderValue so it stays there
  const prevHoverRef = React.useRef(hoverTimestamp);
  useEffect(() => {
    if (prevHoverRef.current !== null && prevHoverRef.current !== undefined && (hoverTimestamp === null || hoverTimestamp === undefined)) {
      setSliderValue(lastSliderPercentRef.current);
    }
    prevHoverRef.current = hoverTimestamp;
  }, [hoverTimestamp]);

  const isGpsAltitude = selectedChannel === GPS_ALTITUDE_KEY;

  // Pre-parse and sort measurements
  const parsedMeasurements = React.useMemo(() => {
    return (measurements ?? []).map(m => ({
      ...m,
      timeMs: new Date(m.timestamp).getTime(),
    }));
  }, [measurements]);

  const channelMeasurements = React.useMemo(() => {
    if (isGpsAltitude) return [];
    return parsedMeasurements
      .filter(m => m.channel === selectedChannel)
      .sort((a, b) => a.timeMs - b.timeMs);
  }, [parsedMeasurements, isGpsAltitude, selectedChannel]);

  // Memoize segment colors so we don't recalculate O(N+M) items on every slider tick
  const { coloredSegments, minVal, maxVal } = React.useMemo(() => {
    if (coordinates.length === 0) return { coloredSegments: null, minVal: 0, maxVal: 1 };

    const altitudeValues = coordinates.map(c => c.altitude);
    const channelValues = isGpsAltitude
      ? altitudeValues
      : channelMeasurements.map(m => transformChannelValue(selectedChannel, m.value) ?? m.value);
    const minVal = channelValues.length > 0 ? Math.min(...channelValues) : 0;
    const maxVal = channelValues.length > 0 ? Math.max(...channelValues) : 1;

    if (!showSensorOverlay || (!isGpsAltitude && channelMeasurements.length === 0)) {
      return { coloredSegments: null, minVal, maxVal };
    }

    let mIdx = 0;
    const segments = coordinates.slice(0, -1).map((coord, i) => {
      let value: number | undefined;
      
      if (isGpsAltitude) {
        value = coord.altitude;
      } else {
        const t = new Date(coord.timestamp).getTime();
        while (mIdx < channelMeasurements.length - 1) {
          const currentDist = Math.abs(channelMeasurements[mIdx].timeMs - t);
          const nextDist = Math.abs(channelMeasurements[mIdx + 1].timeMs - t);
          if (nextDist <= currentDist) {
            mIdx++;
          } else {
            break;
          }
        }
        const rawVal = channelMeasurements[mIdx]?.value;
        value = transformChannelValue(selectedChannel, rawVal);
      }
      
      const color = value !== undefined ? valueToColor(value, minVal, maxVal) : '#aaaaaa';
      
      return {
        positions: [
          [coord.latitude, coord.longitude] as [number, number],
          [coordinates[i + 1].latitude, coordinates[i + 1].longitude] as [number, number],
        ],
        color,
      };
    });

    return { coloredSegments: segments, minVal, maxVal };
  }, [coordinates, channelMeasurements, isGpsAltitude, selectedChannel, showSensorOverlay]);

  const currentValue = showSensorOverlay && coordinates.length > 0
    ? isGpsAltitude
      ? currentCoordinate.altitude
      : transformChannelValue(selectedChannel, findNearestValue(currentCoordinate.timestamp, channelMeasurements))
    : undefined;

  const handleLineClick = React.useCallback((e: L.LeafletMouseEvent) => {
    const { lat, lng } = e.latlng;
    let nearestIdx = 0;
    let nearestDist = Infinity;
    coordinates.forEach((c, i) => {
      const d = (c.latitude - lat) ** 2 + (c.longitude - lng) ** 2;
      if (d < nearestDist) { nearestDist = d; nearestIdx = i; }
    });
    setCalloutIndex(prev => prev === nearestIdx ? null : nearestIdx);
  }, [coordinates]);

  if (coordinates.length === 0) {
    return (
      <div style={{
        width: '100%',
        height: '300px',
        background: '#f0f0f0',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span style={{ color: '#687088' }}>Keine Koordinaten verfügbar</span>
      </div>
    );
  }

  // Create custom icons (doubled size)
  const startIcon = L.icon({
    iconUrl: mapStartIcon,
    iconSize: [64, 64],
    iconAnchor: [32, 32],
  });

  const aircraftIcon = L.icon({
    iconUrl: aircraftType === 'ballon' ? mapBallonIcon : mapPlaneIcon,
    iconSize: [64, 64],
    iconAnchor: [32, 32],
  });

  // Format time
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeFull = (date: Date) =>
    date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const formatCoord = (lat: number, lon: number) =>
    `${lat.toFixed(5)}°, ${lon.toFixed(5)}°`;

  const selectedConfig = getChannelConfig(selectedChannel);
  const unit = selectedConfig.unit;

  return (
    <div>
      <div style={{ borderRadius: '8px', overflow: 'hidden', height }}>
        <MapContainer
          center={[centerLat, centerLon]}
          zoom={11}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          {/* Base Layer - OpenStreetMap */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* OpenFlightMap Overlay - Germany (ED) */}
          <TileLayer
            attribution='&copy; <a href="https://www.openflightmaps.org/">open flightmaps association</a>'
            url="https://nwy-tiles-api.prod.newaydata.com/tiles/{z}/{x}/{y}.png?path=2602/aero/latest"
            minZoom={7}
            maxZoom={13}
            opacity={0.6}
          />

          {/* Flight Path — rendered by memoized layer to avoid re-rendering polylines on marker movement */}
          <FlightPathLayer
            coloredSegments={coloredSegments}
            pathCoordinates={pathCoordinates}
            onLineClick={handleLineClick}
            selectedChannel={selectedChannel}
          />

          {/* Start Marker */}
          <Marker
            position={[coordinates[0].latitude, coordinates[0].longitude]}
            icon={startIcon}
          />

          {/* Aircraft/Balloon Position Marker */}
          <Marker
            position={[currentCoordinate.latitude, currentCoordinate.longitude]}
            icon={aircraftIcon}
          />

          {/* Callout — shown on flight line click */}
          {showSensorOverlay && calloutIndex !== null && (() => {
            const coord = coordinates[calloutIndex];
            const rawVal = isGpsAltitude
              ? coord.altitude
              : findNearestValue(coord.timestamp, channelMeasurements);
            const val = isGpsAltitude
              ? coord.altitude
              : transformChannelValue(selectedChannel, rawVal);
            return (
              <CircleMarker
                center={[coord.latitude, coord.longitude]}
                radius={6}
                pathOptions={{ color: val !== undefined ? valueToColor(val, minVal, maxVal) : '#0d6efd', weight: 2, fillColor: 'white', fillOpacity: 1 }}
              >
                <Tooltip permanent direction="top" offset={[0, -8]}>
                  <div style={{ fontSize: '12px', lineHeight: '1.5', whiteSpace: 'nowrap' }}>
                    {val !== undefined && (
                      <div style={{ fontWeight: 700, color: valueToColor(val, minVal, maxVal) }}>
                        {val.toFixed(selectedConfig.decimals)}{unit ? ` ${unit}` : ''}
                      </div>
                    )}
                    <div style={{ color: '#555' }}>{formatTimeFull(new Date(coord.timestamp))}</div>
                    <div style={{ color: '#555' }}>{formatCoord(coord.latitude, coord.longitude)}</div>
                  </div>
                </Tooltip>
              </CircleMarker>
            );
          })()}

          {/* Dismiss callout on map background click */}
          {calloutIndex !== null && <DismissCallout onDismiss={() => setCalloutIndex(null)} />}

          <ScaleControl position="bottomright" metric={true} imperial={false} maxWidth={150} />

          {/* Fit bounds to flight path */}
          <FitBounds coordinates={coordinates} />
        </MapContainer>
      </div>

      {/* Sensor Overlay Controls — only in expanded mode when data is available */}
      {showSensorOverlay && (
        <div style={{ paddingTop: '12px' }}>
          {/* Channel selector + live reading */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <select
              value={selectedChannel}
              onChange={e => setSelectedChannel(e.target.value)}
              className="form-select form-select-sm"
              style={{ width: 'auto', fontSize: '13px' }}
            >
              {channels.map(ch => (
                <option key={ch} value={ch}>
                  {getChannelConfig(ch).label}
                </option>
              ))}
            </select>
            {currentValue !== undefined && (
              <span style={{
                fontSize: '15px',
                fontWeight: 700,
                color: valueToColor(currentValue, minVal, maxVal),
              }}>
                {currentValue.toFixed(selectedConfig.decimals)}{unit ? ` ${unit}` : ''}
              </span>
            )}
          </div>
          {currentValue !== undefined && (
            <div style={{ fontSize: '12px', color: '#687088', marginBottom: '4px' }}>
              {formatTimeFull(new Date(currentCoordinate.timestamp))} &nbsp;·&nbsp; {formatCoord(currentCoordinate.latitude, currentCoordinate.longitude)}
            </div>
          )}

          {/* Colour scale with min/max labels */}
          {(isGpsAltitude || channelMeasurements.length > 0) && (
            <div style={{ marginBottom: '4px' }}>
              <div style={{
                height: '8px',
                borderRadius: '4px',
                background: 'linear-gradient(to right, hsl(240,80%,45%), hsl(120,80%,45%), hsl(0,80%,45%))',
                marginBottom: '4px',
              }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#687088' }}>
                <span>{minVal.toFixed(selectedConfig.decimals)}{unit ? ` ${unit}` : ''}</span>
                <span>{maxVal.toFixed(selectedConfig.decimals)}{unit ? ` ${unit}` : ''}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Time Slider */}
      <div style={{ padding: '8px 0' }}>
        <input
          type="range"
          min="0"
          max="100"
          value={displaySliderValue}
          onChange={(e) => setSliderValue(Number(e.target.value))}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '14px', color: '#687088' }}>
          <span>{formatTime(startTime)}</span>
          <span>{formatTime(endTime)}</span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(RouteMap);
